import { useReducer, useCallback } from "react";

type PanelSizes = Record<string, string | number>;

interface Action {
  type: "SET_SIZE";
  key: string;
  value: string | number;
}

const reducer = (state: PanelSizes, action: Action): PanelSizes => {
  switch (action.type) {
    case "SET_SIZE":
      return { ...state, [action.key]: action.value };
    default:
      return state;
  }
};

/**
 * 패널 사이즈 관리 훅
 * @param cd_menu - 메뉴 코드
 * @param defaultSizes - 기본 사이즈 설정
 */
const usePanelSizes = (
  cd_menu: string,
  defaultSizes: PanelSizes
): [
  PanelSizes,
  (
    seq: number,
    size: string | number,
    options?: { skipStorage?: boolean }
  ) => void,
] => {
  const panelKeys = Object.keys(defaultSizes);

  // 초기 사이즈 로드
  const menuSize = (() => {
    try {
      const saved = localStorage.getItem("menuSize");
      const parsed = saved ? JSON.parse(saved) : {};
      return parsed?.[cd_menu] || {};
    } catch {
      return {};
    }
  })();

  const initialSizes: PanelSizes = {};
  panelKeys.forEach((key) => {
    initialSizes[key] = menuSize[key] ?? defaultSizes[key];
  });

  const [state, dispatch] = useReducer(reducer, initialSizes);

  const resize = useCallback(
    (
      seq: number,
      size: string | number,
      options?: { skipStorage?: boolean }
    ) => {
      const key = panelKeys[seq - 1];
      if (!key) return;

      dispatch({ type: "SET_SIZE", key, value: size });

      if (!options?.skipStorage) {
        const saved = localStorage.getItem("menuSize");
        const parsed = saved ? JSON.parse(saved) : {};
        parsed[cd_menu] = {
          ...(parsed[cd_menu] || {}),
          [key]: size,
        };
        localStorage.setItem("menuSize", JSON.stringify(parsed));
      }
    },
    [cd_menu, panelKeys]
  );

  return [state, resize];
};

export default usePanelSizes;
